import { useEffect } from 'react';
import { SignedIn, SignedOut, SignInButton, SignUpButton, UserButton } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import Landing from './pages/Landing';
import Sandbox from './pages/Sandbox';
import Agent from './pages/Agent';
import AgentDetail from './pages/AgentDetail';
import Agents from './pages/Agents';
import Create from './pages/Create';
import Run from './pages/Run';
import Dashboard from './pages/Dashboard';
import QuickStart from './pages/QuickStart';
import New from './pages/New';
import Jobs from './pages/Jobs';
import Job from './pages/Job';
import CostExplorer from './pages/CostExplorer';
import Billing from './pages/Billing';
import Status from './pages/Status';
import Handle from './pages/Handle';
import Qpms from './pages/Qpms';
import ProxyTable from './pages/ProxyTable';
import MetricsDashboard from './pages/MetricsDashboard';
import EvalsDashboard from './pages/EvalsDashboard';

import { GlobalProvider } from './providers/GlobalProvider.jsx';
import { useGlobal } from './state';
import { Button } from './components/input';
import { FFSignInButton } from './components/signin';
import { Code } from './components/code';
import { ApiKey, CreditBalance } from './components/user';
import { track } from './components/track';
import { PageTitle } from './components/title';
import { calendarUrl, docsUrl, headerHeight } from './constants';

import * as Sentry from '@sentry/react';

import './App.css';

let didInitSentry = false;

try {
  if (!didInitSentry) {
    Sentry.init({
      dsn: 'https://613559f1b925aa10ef3fb6979fa83ba6@o4509435157151754.ingest.us.sentry.io/4509435158593536',
      enabled: !window.location.href.includes('localhost'),
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      integrations: [
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      // Session Replay
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
    });
  }
} finally {
  didInitSentry = true;
}

const mustLogin = (el, props = {}) => <OnlySignedIn {...props}>{el}</OnlySignedIn>;

const queryClient = new QueryClient();

const staticTitles = {
  '/': 'AI Web Scraping',
  '/sandbox': 'API Sandbox',
  '/new': 'New Agent',
  '/new2': 'New Agent',
  '/agents': 'Agents',
  '/agents/create': 'Create Agent',
  '/dashboard': 'Dashboard',
  '/quick-start': 'Quick Start',
  '/jobs': 'Jobs',
  '/costs': 'Cost Explorer',
  '/billing': 'Billing',
  '/status': 'Proxy Status',
  '/handle': 'Signing In',
  '/metrics/dashboard': 'Metrics',
  '/metrics/evals': 'Evaluations',
  '/metrics/qpms': 'Queries per Minute',
  '/metrics/proxy-table': 'Proxy Table',
};

const titleForPath = (pathname) => {
  if (pathname.startsWith('/agents/run/')) return 'Run Agent';
  if (pathname.startsWith('/agents/')) return 'Agent';
  if (pathname.startsWith('/jobs/')) return 'Job';
  return staticTitles[pathname];
};

function App() {
  return (
    <GlobalProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Inner />
        </BrowserRouter>
      </QueryClientProvider>
    </GlobalProvider>
  );
}

const MavaWidget = () => {
  const { user } = useGlobal();

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://widget.mava.app';
    script.defer = true;
    script.id = 'MavaWebChat';
    script.setAttribute('widget-version', 'v2');
    script.setAttribute('enable-sdk', 'true');
    script.setAttribute('data-token', '214966c8b5988993b7886744c02e21801e80d2cf45abb788e2d7deddc6bbf770');

    document.body.appendChild(script);

    return () => {
      document.getElementById('MavaWebChat')?.remove();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    const identify = () => {
      if (!window.Mava) {
        console.error('No window mava');
      }

      window.Mava.initialize();
      window.Mava.identify({
        emailAddress: user.email,
      });
    };

    window.setTimeout(identify, 5000);
  }, [user]);

  return null;
};

const OnlySignedIn = ({ children, signInText, ...props }) => {
  const node = (
    <div>
      <div style={{ width: 400, margin: '40px auto', border: '1px solid #ccc', borderRadius: 20, padding: 40 }}>
        <div style={{ fontWeight: 'bold', fontSize: '120%', marginBottom: 10 }}>Sign In</div>
        <p>Click the button below to sign in and use FetchFox</p>
        <FFSignInButton {...props}>{signInText || 'Sign In'}</FFSignInButton>
      </div>
    </div>
  );

  return (
    <div>
      <SignedIn>{children}</SignedIn>
      <SignedOut>{node}</SignedOut>
    </div>
  );
};

const Inner = () => {
  const { apiKey, host, modalCounts } = useGlobal();
  const location = useLocation();

  useEffect(() => {
    console.log('modalCounts.count is now', modalCounts.count, document.body);
    if (modalCounts.count) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
  }, [modalCounts.count]);

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    window.setTimeout(() => {
      document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView();
    }, 0);
  }, [location.pathname, location.hash]);

  const style = { display: 'flex', flexDirection: 'column', gap: 10 };
  if (modalCounts.count) {
    style.overflow = 'hidden';
  }

  return (
    <div style={style}>
      <PageTitle title={titleForPath(location.pathname)} />
      <header
        style={{
          borderBottom: '1px solid #ccc',
          display: 'flex',
          alignItems: 'center',
          position: 'fixed',
          gap: 10,
          top: 0,
          left: 0,
          zIndex: 100,
          width: '100vw',
          background: 'white',
          padding: '10px 15px',
          height: headerHeight,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <Link to="/">
            <img src="/logo.svg" style={{ height: 30 }} />
          </Link>

          {/*
          <div>
            <Button trans black to="/">
              Home
            </Button>
          </div>
          <div>
            <Button trans black to="/jobs">
              Jobs
            </Button>
          </div>
          */}
        </div>

        <div style={{ flexGrow: 1 }}></div>

        <div style={{ fontSize: 12, fontFamily: 'monospace' }}>
          {apiKey && (
            <span>
              API Key:
              <ApiKey />
            </span>
          )}
        </div>

        <Link to="/billing">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 12,
              fontFamily: 'monospace',
              cursor: 'pointer',
              color: 'black',
            }}
          >
            <div>Credits:</div>
            <CreditBalance />
          </div>
        </Link>

        <div>
          <Button grayFlat small black to="/#pricing" onClick={() => track('billing/header_pricing')}>
            Pricing
          </Button>
        </div>

        <div>
          <Button grayFlat small black href={calendarUrl}>
            Book a Call
          </Button>
        </div>

        <div>
          <Button grayFlat small black href={docsUrl}>
            Docs
          </Button>
        </div>

        <SignedOut>
          <div style={{ display: 'flex', gap: 10 }}>
            <SignInButton forceRedirectUrl="/handle">
              <Button black grayFlat small>
                Sign In
              </Button>
            </SignInButton>
            <SignUpButton forceRedirectUrl="/handle">
              <Button small>Sign Up</Button>
            </SignUpButton>
          </div>
        </SignedOut>

        <SignedIn>
          {['/', '/sandbox'].includes(location.pathname) && (
            <Button small to="/dashboard">
              Dashboard &raquo;
            </Button>
          )}
          <UserButton />
        </SignedIn>
      </header>

      <main style={{ marginTop: 60, marginBottom: 200 }}>
        <Routes>
          <Route path="/" element={<Landing />} />

          <Route path="/sandbox" element={<Sandbox />} />

          <Route path="/new2" element={mustLogin(<Agent />)} />
          <Route path="/new" element={<Agent />} />
          <Route path="/agents" element={mustLogin(<Agents />)} />
          <Route path="/agents/create" element={<Create />} />
          <Route path="/agents/run/:agentId" element={<Run />} />
          <Route key={window.location.pathname} path="/agents/:id" element={mustLogin(<AgentDetail />)} />

          <Route
            path="/dashboard"
            element={mustLogin(<Dashboard />, {
              signUpFallbackRedirectUrl: '/quick-start',
              signInFallbackRedirectUrl: '/dashboard',
            })}
          />
          <Route path="/quick-start" element={mustLogin(<QuickStart />)} />
          <Route path="/jobs" element={mustLogin(<Jobs />)} />
          <Route path="/costs" element={mustLogin(<CostExplorer />)} />
          <Route path="/billing" element={mustLogin(<Billing />)} />
          <Route key={window.location.pathname} path="/jobs/:id" element={mustLogin(<Job />)} />
          <Route path="/status" element={mustLogin(<Status />)} />

          <Route path="/handle" element={mustLogin(<Handle />)} />

          <Route path="/metrics/dashboard" element={mustLogin(<MetricsDashboard />)} />
          <Route path="/metrics/evals" element={mustLogin(<EvalsDashboard />)} />
          <Route path="/metrics/qpms" element={mustLogin(<Qpms />)} />
          <Route path="/metrics/proxy-table" element={mustLogin(<ProxyTable />)} />
        </Routes>
      </main>
      <ToastContainer theme="light" position="bottom-center" />

      {/* <MavaWidget /> */}

      <SignedIn>
        <script
          dangerouslySetInnerHTML={{
            __html: `!function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js?pixel_id=t2_bi1ydc4t",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);rdt('init','t2_bi1ydc4t');rdt('track', 'PageVisit'); `,
          }}
        />
      </SignedIn>
    </div>
  );
};

export default App;
